"use client"

import Link from "next/link"
import { IconArrowRight, IconChevronRight, IconReceiptOff } from "@tabler/icons-react"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusBadge } from "@/components/invoices/status-badge"
import { useCurrency, formatCurrencyStatic } from "@/hooks/use-currency"

import type { Invoice } from "@/types"

interface RecentInvoicesListProps {
  /** Newest-first slice (up to 5 rows); `null` while unfetched. */
  data: Invoice[] | null
  isLoading: boolean
  /** Extra classes merged onto the panel. */
  className?: string
}

/**
 * Latest invoicing activity — the "what just happened" counterpart to the
 * overdue chase list. Rows carry the canonical {@link StatusBadge} so the chip
 * vocabulary (draft / sent / partial / paid / void + the layered OVERDUE
 * marker) matches the /invoices index exactly rather than re-deriving tones.
 *
 * PRESENTATIONAL ONLY — the caller owns the `getRecentInvoices()` fetch and the
 * `invoices.view` gate.
 */
export function RecentInvoicesList({ data, isLoading, className }: RecentInvoicesListProps) {
  const t = useTranslations("dashboard")
  const formatter = useFormatter()
  const { currency } = useCurrency()

  const rows = data ?? []

  const money = (amount: number, invoiceCurrency?: string) =>
    formatCurrencyStatic(amount, invoiceCurrency || currency, { decimals: false })

  const createdLabel = (createdAt: string): string | null => {
    const date = new Date(createdAt)
    if (Number.isNaN(date.getTime())) return null
    return formatter.relativeTime(date)
  }

  const footer = (
    <div className="mt-3 border-t border-border pt-3">
      <Link
        href="/invoices"
        className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        {t("recentInvoices.viewAll")}
        <IconArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  )

  if (isLoading) {
    return (
      <ApolloPanel eyebrow={t("recentInvoices.title")} className={className}>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-4 w-20 shrink-0" />
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-5 w-16 shrink-0 rounded-full" />
              <Skeleton className="h-4 w-20 shrink-0" />
            </div>
          ))}
        </div>
      </ApolloPanel>
    )
  }

  if (rows.length === 0) {
    return (
      <ApolloPanel eyebrow={t("recentInvoices.title")} className={className}>
        <div className="flex h-[180px] flex-col items-center justify-center text-center">
          <div className="mb-3 rounded-full bg-muted p-3">
            <IconReceiptOff className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            {t("recentInvoices.empty.title")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground/70">
            {t("recentInvoices.empty.body")}
          </p>
        </div>
        {footer}
      </ApolloPanel>
    )
  }

  return (
    <ApolloPanel eyebrow={t("recentInvoices.title")} className={className}>
      <div className="-mx-2 space-y-0.5">
        {rows.map((invoice) => {
          const created = createdLabel(invoice.created_at)
          return (
            <Link
              key={invoice.id}
              href={`/invoices/${invoice.id}`}
              className="group flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none"
            >
              <span className="shrink-0 font-mono text-xs font-medium">
                {invoice.invoice_number}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm">
                  {invoice.lead?.full_name ?? t("recentInvoices.noPatient")}
                </span>
                {created && (
                  <span className="block truncate text-[11px] text-text-dim">{created}</span>
                )}
              </span>
              <StatusBadge
                status={invoice.status}
                isOverdue={invoice.is_overdue}
                className="shrink-0 text-[10px]"
              />
              <span className="shrink-0 text-sm font-medium tabular-nums">
                {money(invoice.total_amount, invoice.currency)}
              </span>
              <IconChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
            </Link>
          )
        })}
      </div>
      {footer}
    </ApolloPanel>
  )
}
